import styled, { css } from "styled-components";
import UILabel from "../UILabel";
import { TimelineEventStatus, TimelineEventStepStatus } from "./types";

const getEventStepLineColor = ({
  $status,
  theme,
}: {
  $status?: TimelineEventStepStatus;
  theme: any;
}) =>
  $status === TimelineEventStepStatus.incomplete
    ? theme.colors.secondary
    : theme.colors.accent;

const getNodeColor = ({
  $status,
  theme,
}: {
  $status: TimelineEventStatus;
  theme: any;
}) => {
  if (
    [TimelineEventStatus.future, TimelineEventStatus.next].includes($status)
  ) {
    return theme.colors.secondary;
  }
  return theme.colors.accent;
};

const getLineColor = ({
  $status,
  theme,
}: {
  $status: TimelineEventStatus;
  theme: any;
}) => {
  if (
    [TimelineEventStatus.current, TimelineEventStatus.past].includes($status)
  ) {
    return theme.colors.accent;
  }
  return theme.colors.secondary;
};

export const Wrapper = styled.ol`
  margin: 0;
  padding: 0 16px 0 0;

  list-style-type: none;
`;

export const EventMain = styled.div<{
  $hasSteps?: boolean;
  $stepStatus?: TimelineEventStepStatus;
}>`
  position: relative;

  display: flex;
  gap: 12px;

  padding-bottom: 24px;

  ${(props) =>
    props.$hasSteps &&
    css`
      &::before {
        content: "";

        position: absolute;
        left: 9px;
        top: 10px;

        background: ${getEventStepLineColor({
          $status: props.$stepStatus,
          theme: props.theme,
        })};

        width: 2px;
        height: 100%;
      }
    `}
`;

export const EventNode = styled.div`
  position: relative;
  z-index: 1;

  width: 20px;
  height: 20px;
  flex: 0 0 auto;

  border-radius: 9999px;
  box-shadow: 0 0 0 6px var(--point-color) inset;
  background: white;
`;

export const EventText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
`;

export const EventDate = styled(UILabel.Small)`
  padding-bottom: 2px;
`;

export const EventDescription = styled(UILabel.Medium)`
  &:empty {
    display: none;
  }
`;

export const EventConnector = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  transform: translateX(-50%);

  height: 100%;

  svg {
    height: 100%;
    width: 6px;

    line {
      stroke: var(--line-color);
    }
  }
`;

export const EventStep = styled.li<{
  $status: TimelineEventStepStatus;
  $nextStatus?: TimelineEventStepStatus;
}>`
  position: relative;

  padding-bottom: 24px;

  &::before,
  &::after {
    content: "";

    position: absolute;
    left: -23px;
    top: 10px;
  }

  &::before {
    width: 2px;
    height: 100%;

    background: ${(props) =>
      getEventStepLineColor({
        $status: props.$nextStatus,
        theme: props.theme,
      })};
  }

  &::after {
    width: 18px;
    height: 2px;

    background: ${getEventStepLineColor};
  }

  &:last-child {
    &::before {
      content: none;
    }
  }
`;

export const EventStepMain = styled.div`
  display: flex;
  gap: 8px;
`;

export const EventStepContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
`;

export const EventStepLineItem = styled.div`
  display: flex;
  gap: 8px;
`;

export const EventStepNode = styled.div`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9999px;
`;

export const EventStepText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 2px;
  padding: 2px 0;
`;

export const EventStepDetail = styled.div`
  flex: 0 0 auto;
  padding: 2px 0;
`;

export const Event = styled.li<{ $status: TimelineEventStatus }>`
  position: relative;

  --point-color: ${getNodeColor};
  --line-color: ${getLineColor};

  ${(props) =>
    props.$status === TimelineEventStatus.next &&
    css`
      ${EventNode} {
        box-shadow: 0 0 0 2px ${props.theme.colors.accent} inset,
          0 0 0 6px var(--point-color) inset;
      }
    `}
`;

export const EventSteps = styled.ol`
  margin: 0;
  padding: 0 0 0 32px;

  list-style-type: none;

  ${EventStep} {
    &:last-child {
      padding-bottom: 0;
    }
  }
`;
